/**
 * @fileoverview Scope-aware SAS URL resolver for uploaded files.
 * @module file-service-client/urlResolver
 *
 * Responsibilities: - Resolve uploaded-file IDs to short-lived SAS URLs.
 *                   - Cache resolved URLs (LRU) to avoid redundant HTTP round-trips.
 *                   - Handle scope: try tenant-api first, fall back to system-api
 *                     (for files uploaded via system-web, e.g. department logos).
 * Dependencies:     - FileServiceApiClient (injected by host app bootstrap).
 * Architecture:     - Singleton resolver initialized once per app via initFileUrlResolver().
 *                   - Mirrors @firesquirrel/attachments AttachmentUrlResolver pattern.
 * Notes:            - SAS URLs are cached for 14 minutes (server issues 60-min tokens,
 *                     but we refresh early to avoid edge-case expiry during long sessions).
 *                   - The resolver is scope-agnostic: it tries both APIs and caches
 *                     whichever one succeeds. This handles the case where a tenant-web
 *                     document references a system-uploaded file (e.g. department logo).
 */

/**
 * @contract FileServiceApiClient
 * Purpose: API client interface that each host app implements to fetch SAS URLs.
 * Fields:  getTenantFileDownloadUrl — tenant-api endpoint for tenant-scoped files.
 *          getSystemFileDownloadUrl — system-api endpoint for system-scoped files.
 * Rules:   Implementations may throw when the file is not found in that scope;
 *          the resolver catches and falls back to the other scope.
 * Sync:    Inherently-online exception — SAS URLs cannot be sourced from Dexie.
 */
export interface FileServiceApiClient {
  /** GET /api/files/:id/download → { downloadUrl } (tenant-api). */
  getTenantFileDownloadUrl(fileId: string): Promise<string>;
  /** GET /api/system/files/:id/download → { downloadUrl } (system-api proxy). */
  getSystemFileDownloadUrl(fileId: string): Promise<string>;
}

interface CacheEntry {
  url: string;
  expiresAt: number;
}

const LRU_MAX = 256;
const SAS_TTL_MS = 14 * 60 * 1000; // refresh before the 60-min server token expires

/**
 * @localFirst FileUrlResolver
 * Source of Truth: hybrid — Dexie stores file metadata; SAS URLs are inherently-online
 *                  (private Azure blobs require server-issued tokens).
 * Write Flow:      read-only (no Dexie writes).
 * Sync Strategy:   LRU cache with 14-min TTL avoids redundant HTTP round-trips within
 *                  a session; cache misses fall through to the API.
 * Conflict Resolution: none — URLs are short-lived and read-only.
 */
export class FileUrlResolver {
  private cache = new Map<string, CacheEntry>();

  constructor(private readonly api: FileServiceApiClient) {}

  /**
   * Resolve an uploaded-file ID to a displayable SAS URL.
   *
   * Resolution order:
   *   1. Check cache — return if still valid.
   *   2. Try tenant-api (the common case for tenant-web).
   *   3. Fall back to system-api (for system-scoped files like department logos).
   *
   * Returns null when both APIs fail (file not found, storage not configured, etc.).
   */
  async resolve(fileId: string): Promise<string | null> {
    const hit = this.cache.get(fileId);
    if (hit && hit.expiresAt > Date.now()) return hit.url;
    if (hit) this.cache.delete(fileId);

    // Try tenant-api first (handles the common case: tenant-uploaded files).
    try {
      const url = await this.api.getTenantFileDownloadUrl(fileId);
      this.storeEntry(fileId, url);
      return url;
    } catch {
      // Not in tenant DB — try system-api (department logos, system templates, etc.).
    }

    try {
      const url = await this.api.getSystemFileDownloadUrl(fileId);
      this.storeEntry(fileId, url);
      return url;
    } catch {
      // File not found in either API — leave unresolved.
      return null;
    }
  }

  /**
   * Resolve multiple file IDs in parallel. More efficient than calling resolve()
   * individually because it batches the HTTP requests.
   */
  async resolveMany(fileIds: string[]): Promise<Record<string, string>> {
    const entries = await Promise.all(
      fileIds.map(async (fid): Promise<readonly [string, string | null]> => {
        const url = await this.resolve(fid);
        return [fid, url] as const;
      }),
    );
    const result: Record<string, string> = {};
    for (const [fid, url] of entries) {
      if (url) result[fid] = url;
    }
    return result;
  }

  /** Invalidate a specific cache entry (e.g. after a file replacement). */
  invalidate(fileId: string): void {
    this.cache.delete(fileId);
  }

  /** Clear all cached URLs. */
  clear(): void {
    this.cache.clear();
  }

  // -----------------------------------------------------------------------

  private storeEntry(key: string, url: string): void {
    if (this.cache.size >= LRU_MAX) {
      const oldest = this.cache.keys().next().value;
      if (oldest !== undefined) this.cache.delete(oldest);
    }
    this.cache.set(key, { url, expiresAt: Date.now() + SAS_TTL_MS });
  }
}

// ---------------------------------------------------------------------------
// Singleton management — mirrors @firesquirrel/attachments pattern.
// ---------------------------------------------------------------------------

let singleton: FileUrlResolver | null = null;

/**
 * Initialize the singleton URL resolver. Call once per app during bootstrap
 * (before any component that uses useUploadedFileUrl mounts).
 */
export function initFileUrlResolver(api: FileServiceApiClient): FileUrlResolver {
  singleton = new FileUrlResolver(api);
  return singleton;
}

/**
 * Retrieve the initialized resolver. Throws if initFileUrlResolver() hasn't
 * been called — fail-fast so missing bootstrap is caught immediately.
 */
export function getFileUrlResolver(): FileUrlResolver {
  if (!singleton) {
    throw new Error(
      '[file-service] URL resolver not initialized. Call initFileUrlResolver() during app bootstrap.',
    );
  }
  return singleton;
}

/**
 * Safe variant that returns null instead of throwing. Useful for components
 * that may mount before bootstrap completes (e.g. during tests).
 */
export function tryGetFileUrlResolver(): FileUrlResolver | null {
  return singleton;
}
